import { api } from '../../api/client';
import type { QrArea } from '../../platform/qrPolarity';
import type { Layout } from './designer/layout';
import type { SavedPrinterProfileSettings } from './printerProfile';

export type PrintTarget = 'pdf' | 'niimbot_d110';
export type PrintJobStatus = 'queued' | 'claimed' | 'printed' | 'failed' | 'cancelled';
/** One label per page (a roll printer) or tiled onto A4 with cut guides. */
export type SheetMode = 'labels' | 'sheet';

export interface LabelTemplate {
  id: string;
  code: string;
  name_ru: string;
  name_en: string;
  entity_kind: string;
  target: PrintTarget;
  media_width_mm: number;
  media_height_mm: number;
  dpi: number;
  /** Labels the roll carries side by side, and the gap between them. */
  across: number;
  gap_mm: number;
  /** Quarter turns applied to the finished page. Which way round a label leaves
   *  a printer is a fact about that printer's driver, so it is a setting. */
  rotate: number;
  is_default: boolean;
  is_enabled: boolean;
  /** Shipped by us. Its layout is not editable — a re-seed would undo the edit —
   *  so the designer offers a copy instead. */
  is_seeded: boolean;
  layout: Layout;
  /** Component types this template is not offered for. Empty fits everything. */
  excluded_type_ids: string[];
  /** The part printing spends: the label itself, kept in the catalogue. */
  stock_component_id: string | null;
  stock_location_id: string | null;
}

export interface TemplateChanges {
  name_ru?: string;
  name_en?: string;
  media_width_mm?: number;
  media_height_mm?: number;
  dpi?: number;
  across?: number;
  gap_mm?: number;
  rotate?: number;
  layout?: Layout;
  is_default?: boolean;
  is_enabled?: boolean;
  excluded_type_ids?: string[];
  /** `null` unbinds; absent leaves it alone. */
  stock_component_id?: string | null;
  stock_location_id?: string | null;
}

export interface TemplateNew extends TemplateChanges {
  code: string;
  name_ru: string;
  name_en: string;
  entity_kind?: string;
  target?: PrintTarget;
  /** Take the layout and the roll from this one. How a shipped template
   *  becomes an editable one without retyping twenty coordinates. */
  copy_of?: string;
}

/** What a print run spent, or why it spent nothing. Never an error: the labels
 *  are already off the roll by the time this is answered. */
export interface Consumed {
  consumed: boolean;
  component_id: string | null;
  quantity: string | null;
  remaining: string | null;
  reason: string | null;
}

export interface TemplateStock {
  component_id: string | null;
  code: string | null;
  name: string | null;
  remaining: string | null;
}

export interface PrintJob {
  id: string;
  template_id: string;
  target: PrintTarget;
  entity_kind: string;
  entity_id: string;
  title: string | null;
  copies: number;
  status: PrintJobStatus;
  created_at: string;
  claimed_at: string | null;
  error: string | null;
}

/**
 * The templates on offer.
 *
 * With `entityId` the server narrows them to what suits that record's type: the
 * client holds a part, not a type, and working the narrowing out here would
 * mean fetching the part and every template's blacklist first.
 */
export const listTemplates = (entityKind?: string, entityId?: string, includeHidden = false) => {
  const params = new URLSearchParams();
  if (entityKind) params.set('entity_kind', entityKind);
  if (entityId) params.set('entity_id', entityId);
  if (includeHidden) params.set('include_hidden', 'true');
  const query = params.toString();
  return api.get<LabelTemplate[]>(`/labels/templates${query ? `?${query}` : ''}`);
};

/** Correct the roll size, the rotation, the layout or what it spends. */
export const updateTemplate = (id: string, changes: TemplateChanges) =>
  api.patch<LabelTemplate>(`/labels/templates/${id}`, changes);

export const createTemplate = (input: TemplateNew) =>
  api.post<LabelTemplate>('/labels/templates', input);

export const deleteTemplate = (id: string) => api.delete<void>(`/labels/templates/${id}`);

export const templateStock = (id: string) =>
  api.get<TemplateStock>(`/labels/templates/${id}/stock`);

/**
 * Labels came off this template just now — write them off.
 *
 * For the two paths with no queue behind them: the tape printer, which knows
 * the moment the machine took the raster, and "print now", where the person
 * answers for the browser's own dialog.
 */
export const reportPrinted = (templateId: string, count: number) =>
  api.post<Consumed>('/labels/printed', { template_id: templateId, count });

/** The raster of a layout nobody has saved yet — what the designer draws
 *  against. The server's own picture, not a browser's idea of one. */
export const draftPreview = (input: {
  layout: Layout;
  media_width_mm: number;
  media_height_mm: number;
  dpi?: number;
  entity_kind?: string;
  entity_id?: string | null;
}) => api.postBlob('/labels/preview.png', input);

/** The exact bitmap that will be printed. Shown before printing because a label
 *  is consumable: an ellipsised name is worth catching on screen, not on the roll. */
export const previewUrl = (templateId: string, entityId: string) =>
  `/labels/preview.png?template_id=${templateId}&entity_id=${entityId}`;

export interface LabelBitmap { blob: Blob; qrAreas: QrArea[] }

function labelBitmap(result: { blob: Blob; headers: Headers }): LabelBitmap {
  try {
    const parsed: unknown = JSON.parse(result.headers.get('X-StorageAPP-QR-Areas') ?? '[]');
    if (!Array.isArray(parsed)) throw new Error('not an array');
    const qrAreas = parsed.flatMap((area): QrArea[] => {
      if (!Array.isArray(area) || area.length !== 4 || !area.every((value) => typeof value === 'number' && Number.isFinite(value))) return [];
      const [x, y, w, h] = area;
      return w > 0 && h > 0 ? [{ x, y, w, h, unit: 'px' }] : [];
    });
    return { blob: result.blob, qrAreas };
  } catch {
    return { blob: result.blob, qrAreas: [] };
  }
}

/** The exact saved-label bitmap, including each QR's final server-drawn bounds. */
export const previewBitmap = async (templateId: string, entityId: string): Promise<LabelBitmap> =>
  labelBitmap(await api.blobWithHeaders(previewUrl(templateId, entityId)));

export const renderPdf = (input: {
  template_id: string;
  entity_ids: string[];
  copies?: number;
  mode?: SheetMode;
}) => api.postBlob('/labels/pdf', input);

export interface FreeformPrintInput {
  template_id: string;
  fields: Record<string, string>;
  copies?: number;
  mode?: SheetMode;
}

/** A one-off label has no catalogue record. Its text is rendered for this
 * immediate print only and is deliberately never placed in the print queue. */
export const freeformPreview = (input: FreeformPrintInput) =>
  api.postBlob('/labels/freeform/preview.png', input);

export const freeformBitmap = async (input: FreeformPrintInput): Promise<LabelBitmap> =>
  labelBitmap(await api.postBlobWithHeaders('/labels/freeform/preview.png', input));

export const renderFreeformPdf = (input: FreeformPrintInput) =>
  api.postBlob('/labels/freeform/pdf', input);

export const calibrationPdf = (templateId: string) =>
  api.blob(`/labels/calibration.pdf?template_id=${templateId}`);

/** Direct-TSPL ruler generated at the physical size of the configured label. */
export const directCalibrationBitmap = async (widthMm = 104, heightMm = 80): Promise<LabelBitmap> =>
  labelBitmap(await api.blobWithHeaders(`/labels/calibration.png?width_mm=${encodeURIComponent(widthMm)}&height_mm=${encodeURIComponent(heightMm)}`));

export interface SavedPrinterProfile {
  id: string;
  settings: SavedPrinterProfileSettings;
  url: string;
  created_at: string;
}

/** Server-side snapshot used by the QR on a completed calibration label. */
export const savePrinterProfileSnapshot = (settings: SavedPrinterProfileSettings) =>
  api.post<SavedPrinterProfile>('/labels/printer-profiles', { settings });

export const getPrinterProfileSnapshot = (id: string) =>
  api.get<SavedPrinterProfile>(`/labels/printer-profiles/${encodeURIComponent(id)}`);

export const printerProfileQr = (id: string) =>
  api.blob(`/labels/printer-profiles/${encodeURIComponent(id)}/qr.png`);

export type PrinterDriver = 'tspl_classic' | 'niimbot_ble' | 'marklife_p15_ble' | 'system_pdf';
export type PrinterModel = 'xp365b' | 'hzzt9260' | 'niimbot_d110' | 'marklife_p15' | 'xp_dt108b';
export type PrinterConnectionKind = 'bluetooth_classic' | 'bluetooth_le' | 'system_usb';
export type PrinterProfileStatus = 'draft' | 'ready';

export type ManagedPrinterSettings =
  | { driver: 'tspl_classic'; polarity_correction: boolean; density: number; speed: number; feed_mm: number; gap_mm: number; gap_offset_mm: number; head_width_mm: number; media_alignment: 'left' | 'center'; x_offset_mm: number; y_offset_mm: number; scale_x: number; scale_y: number }
  | { driver: 'niimbot_ble'; polarity_correction: boolean; dpi: 203; head_width_dots: 96; density: number; max_width_mm: number; max_height_mm: number }
  | { driver: 'marklife_p15_ble'; polarity_correction: boolean; density: number; max_width_mm: number; max_height_mm: number }
  | { driver: 'system_pdf'; polarity_correction: boolean; head_width_mm: number; media_alignment: 'left' | 'center' };

export interface ManagedPrinterProfile {
  id: string;
  short_code: string;
  name: string;
  model: PrinterModel;
  driver: PrinterDriver;
  connection_kind: PrinterConnectionKind;
  status: PrinterProfileStatus;
  is_enabled: boolean;
  settings_version: number;
  settings: ManagedPrinterSettings;
  calibrated_at: string | null;
  created_at: string;
  updated_at: string;
  created_by_id: string | null;
  updated_by_id: string | null;
  template_ids: string[];
  template_count: number;
  url: string;
}

export const listPrinterProfiles = (filters: { enabled?: boolean; status?: PrinterProfileStatus; templateId?: string } = {}) => {
  const params = new URLSearchParams();
  if (filters.enabled !== undefined) params.set('enabled', String(filters.enabled));
  if (filters.status) params.set('status', filters.status);
  if (filters.templateId) params.set('template_id', filters.templateId);
  const query = params.toString();
  return api.get<ManagedPrinterProfile[]>(`/labels/printer-profiles${query ? `?${query}` : ''}`);
};

export const createManagedPrinterProfile = (input: {
  model: PrinterModel; name?: string; settings?: ManagedPrinterSettings; status?: PrinterProfileStatus; is_enabled?: boolean;
}) => api.post<ManagedPrinterProfile>('/labels/printer-profiles', input);

export const updateManagedPrinterProfile = (id: string, input: {
  name?: string; settings?: ManagedPrinterSettings; status?: PrinterProfileStatus; is_enabled?: boolean; calibrated_at?: string | null;
}) => api.patch<ManagedPrinterProfile>(`/labels/printer-profiles/${encodeURIComponent(id)}`, input);

export const replacePrinterProfileTemplates = (id: string, templateIds: string[]) =>
  api.put<ManagedPrinterProfile>(`/labels/printer-profiles/${encodeURIComponent(id)}/templates`, { template_ids: templateIds });

export const getManagedPrinterProfileByCode = (shortCode: string) =>
  api.get<ManagedPrinterProfile>(`/labels/printer-profiles/by-code/${encodeURIComponent(shortCode)}`);

export const enqueue = (input: { template_id: string; entity_id: string; copies?: number }) =>
  api.post<PrintJob>('/labels/queue', input);

export const listQueue = (target?: PrintTarget) =>
  api.get<PrintJob[]>(`/labels/queue${target ? `?target=${target}` : ''}`);

export const printQueue = (jobIds: string[], mode: SheetMode = 'labels') =>
  api.postBlob('/labels/queue/print', { job_ids: jobIds, mode });

export const claimQueue = (jobIds: string[]) =>
  api.post<PrintJob[]>('/labels/queue/claim', { job_ids: jobIds, mode: 'labels' });

export const finishQueue = (jobIds: string[], ok: boolean, error?: string) =>
  api.post<{ jobs: PrintJob[]; consumed: Consumed[] }>('/labels/queue/finish', {
    job_ids: jobIds,
    ok,
    error,
  });

export const cancelJob = (jobId: string) => api.delete<PrintJob>(`/labels/queue/${jobId}`);

/**
 * Hand a PDF to the browser's own print dialog.
 *
 * A hidden iframe rather than a new tab: a tab needs a pop-up allowance the
 * first time and leaves the user looking at a PDF viewer to close afterwards,
 * while the iframe puts the print dialog straight in front of them and cleans
 * itself up. The object URL is released on a timer because revoking it while the
 * dialog is still open leaves the viewer with nothing to print.
 */
export function printBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.src = url;

  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      // Some browsers refuse to drive a PDF plugin from script. Falling back to
      // a plain download is worse than nothing only if it is silent, so open it.
      window.open(url, '_blank');
    }
  };

  document.body.appendChild(frame);
  window.setTimeout(() => {
    frame.remove();
    URL.revokeObjectURL(url);
  }, 60_000);
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
